import {
  createRouter,
  createWebHistory,
  type RouteRecordRaw,
} from 'vue-router'
import { authGuard, privilegeGuard } from './guards'
// Batch A (UI Sprint 2):
import UsersListView from '@/iam/views/UsersListView.vue'
import AccountView from '@/iam/views/AccountView.vue'
/**
 * Streama CMS · Router configuration
 *
 * Every screen from the prototype (streama-cms.html) gets a route
 * here in UI Sprint 1, even though most resolve to PlaceholderView
 * until the corresponding backend sprint lands. This way the
 * navigation IA is fully clickable from day one and Sprint 2+ just
 * swap component imports without touching the route table.
 *
 * Route meta carries:
 *   - `title`        i18n key for the breadcrumb / page title
 *   - `crumb`        full breadcrumb (e.g. "Content · VOD")
 *   - `section`      section ID — drives sidebar grouping
 *   - `icon`         lucide icon name for the sidebar
 *   - `meta`         optional badge value (e.g. "3.2K")
 *   - `privilege`    optional authority required to access — see below
 *   - `sprint`       UI sprint where the real view lands (for placeholders)
 *   - `public`       skip auth guard (login page)
 *
 * ## Privilege naming convention
 *
 * Privilege values here use **bare names** ("videos", "check_users",
 * "offers"). The PRIV_ prefix is an internal Spring Security authority
 * concern on the backend — it is added when the backend's
 * UserDetailsService loads user authorities for @PreAuthorize matching,
 * and is NEVER present on the wire or in the JWT claims.
 *
 * The auth store stores privileges as bare names. useAuthority() and
 * the privilegeGuard compare bare names directly. Do not reintroduce
 * the PRIV_ prefix here unless the backend wire format changes.
 *
 * (This convention was set after the v2 validation runbook found that
 * the prefix mismatch was breaking every privilege-gated route.)
 */
declare module 'vue-router' {
  interface RouteMeta {
    title: string
    crumb: string
    section: SectionId
    icon: string
    badge?: string
    privilege?: string
    sprint?: 1 | 2 | 3
    public?: boolean
    layout?: 'shell' | 'auth' | 'blank'
    hideFromNav?: boolean
    navOrder?: number
    requiredPrivilege?: string
  }
}
/**
 * Section IDs match the prototype's SECTIONS const verbatim. The
 * lens store references these to decide nav order.
 */
export type SectionId =
  | 'overview'
  | 'operations'
  | 'content'
  | 'people'
  | 'storefront'
  | 'audience'
  | 'support'
  | 'revenue'
  | 'partners'
  | 'messaging'
  | 'platform'
/**
 * Section display labels — i18n keys are nav.section.<id>.
 */
export interface SectionDef {
  id: SectionId
  labelKey: string
}
export const SECTIONS: SectionDef[] = [
  { id: 'overview',   labelKey: 'nav.section.overview' },
  { id: 'operations', labelKey: 'nav.section.operations' },
  { id: 'content',    labelKey: 'nav.section.content' },
  { id: 'people',     labelKey: 'nav.section.people' },
  { id: 'storefront', labelKey: 'nav.section.storefront' },
  { id: 'audience',   labelKey: 'nav.section.audience' },
  { id: 'support',    labelKey: 'nav.section.support' },
  { id: 'revenue',    labelKey: 'nav.section.revenue' },
  { id: 'partners',   labelKey: 'nav.section.partners' },
  { id: 'messaging',  labelKey: 'nav.section.messaging' },
  { id: 'platform',   labelKey: 'nav.section.platform' },
]
// Lazy view loader — every placeholder screen shares one component.
// Real views in Sprint 2+ replace these imports with screen-specific
// dynamic imports.
const Placeholder = () => import('@/views/PlaceholderView.vue')
const DashboardStub = () => import('@/views/DashboardStubView.vue')
const Login = () => import('@/views/auth/LoginView.vue')
/**
 * The full screen table. 46 entries — one per prototype view. Order
 * here is the in-section order; lens-driven section order is applied
 * in the Sidebar component.
 */
export const SCREENS: RouteRecordRaw[] = [
  // ===== OVERVIEW =====
  {
    path: '/dashboard',
    name: 'dashboard',
    component: DashboardStub,
    meta: {
      title: 'nav.dashboard',
      crumb: 'Dashboard',
      section: 'overview',
      icon: 'home',
      sprint: 1,
    },
  },
  // ===== OPERATIONS =====
  {
    path: '/operations/health',
    name: 'health',
    component: Placeholder,
    meta: {
      title: 'nav.health',
      crumb: 'Platform · System health',
      section: 'operations',
      icon: 'activity',
      badge: 'LIVE',
      sprint: 3,
    },
  },
  {
    path: '/operations/runbooks',
    name: 'runbooks',
    component: Placeholder,
    meta: {
      title: 'nav.runbooks',
      crumb: 'Platform · Runbooks',
      section: 'operations',
      icon: 'book-open',
      badge: '68',
      sprint: 3,
    },
  },
  {
    path: '/operations/crons',
    name: 'crons',
    component: Placeholder,
    meta: {
      title: 'nav.crons',
      crumb: 'Platform · Scheduled jobs',
      section: 'operations',
      icon: 'clock',
      badge: '42',
      sprint: 3,
    },
  },
  {
    path: '/operations/cache',
    name: 'cache',
    component: Placeholder,
    meta: {
      title: 'nav.cache',
      crumb: 'Platform · Cached reports',
      section: 'operations',
      icon: 'database',
      sprint: 3,
    },
  },
  // ===== CONTENT =====
  {
    path: '/content/vod',
    name: 'vod-library',
    component: () => import('@/catalog/videos/views/VideosListView.vue'),
    meta: {
      title: 'nav.vod',
      crumb: 'Content · VOD library',
      section: 'content',
      icon: 'film',
      badge: '3.2K',
      requiredPrivilege: 'movies',
      navOrder: 30,
      sprint: 2,
    },
  },
  {
    path: '/content/services',
    name: 'services',
    component: () => import('@/catalog/services/views/ServicesListView.vue'),
    meta: {
      title: 'nav.services',
      crumb: 'Content · Services',
      section: 'content',
      icon: 'layers',
      requiredPrivilege: 'services',
      navOrder: 20,
      sprint: 2,
    },
  },
  {
    path: '/content/series',
    name: 'series',
    component: Placeholder,
    meta: {
      title: 'nav.series',
      crumb: 'Content · Series',
      section: 'content',
      icon: 'tv',
      badge: '412',
      sprint: 3,
    },
  },
  {
    path: '/content/movies',
    name: 'movies',
    component: Placeholder,
    meta: {
      title: 'nav.movies',
      crumb: 'Content · Movies',
      section: 'content',
      icon: 'film',
      badge: '2.4K',
      sprint: 2,
    },
  },
  {
    path: '/content/music',
    name: 'music',
    component: Placeholder,
    meta: {
      title: 'nav.music',
      crumb: 'Content · Music',
      section: 'content',
      icon: 'music',
      badge: '18K',
      sprint: 3,
    },
  },
  {
    path: '/content/live',
    name: 'live',
    component: Placeholder,
    meta: {
      title: 'nav.live',
      crumb: 'Content · Live channels',
      section: 'content',
      icon: 'radio',
      badge: '64',
      sprint: 2,
    },
  },
  {
    path: '/content/bundles',
    name: 'bundles',
    component: () => import('@/commerce/bundles/views/BundlesListView.vue'),
    meta: {
      title: 'nav.bundles',
      crumb: 'Content · Bundles',
      section: 'content',
      icon: 'package',
      badge: '28',
      requiredPrivilege: 'bundles',
      navOrder: 40,
      sprint: 3,
    },
  },
  {
    path: '/content/bundles/:id(\\d+)',
    name: 'bundle-detail',
    component: () => import('@/commerce/bundles/views/BundleDetailView.vue'),
    meta: {
      title: 'nav.bundles',
      crumb: 'Content · Bundle detail',
      section: 'content',
      icon: 'package',
      requiredPrivilege: 'bundles',
      hideFromNav: true,
      sprint: 3,
    },
  },
  {
    path: '/content/categories',
    name: 'categories',
    component: () => import('@/catalog/categories/views/CategoriesListView.vue'),
    meta: {
      title: 'nav.categories',
      crumb: 'Content · Categories',
      section: 'content',
      icon: 'tag',
      requiredPrivilege: 'categories',
      navOrder: 10,
      sprint: 2,
    },
  },
  // ===== PEOPLE =====
  {
    path: '/people/cast',
    name: 'cast',
    component: Placeholder,
    meta: {
      title: 'nav.cast',
      crumb: 'People · Cast & crew',
      section: 'people',
      icon: 'users',
      badge: '2.4K',
      sprint: 3,
    },
  },
  {
    path: '/people/teams',
    name: 'teams',
    component: Placeholder,
    meta: {
      title: 'nav.teams',
      crumb: 'People · Teams',
      section: 'people',
      icon: 'shield',
      badge: '412',
      sprint: 3,
    },
  },
  {
    path: '/people/matches',
    name: 'matches',
    component: Placeholder,
    meta: {
      title: 'nav.matches',
      crumb: 'People · Matches',
      section: 'people',
      icon: 'trophy',
      badge: '148',
      sprint: 3,
    },
  },
  {
    path: '/people/youtube',
    name: 'youtube',
    component: Placeholder,
    meta: {
      title: 'nav.youtube',
      crumb: 'People · YouTube',
      section: 'people',
      icon: 'youtube',
      badge: '68',
      sprint: 3,
    },
  },
  // ===== STOREFRONT (App & PWA) =====
  {
    path: '/storefront/appearance',
    name: 'appearance',
    component: Placeholder,
    meta: {
      title: 'nav.appearance',
      crumb: 'App & PWA · Look & feel',
      section: 'storefront',
      icon: 'palette',
      sprint: 3,
    },
  },
  {
    path: '/storefront/menus',
    name: 'menus',
    component: Placeholder,
    meta: {
      title: 'nav.menus',
      crumb: 'App & PWA · Navigation',
      section: 'storefront',
      icon: 'menu',
      sprint: 3,
    },
  },
  {
    path: '/storefront/slides',
    name: 'slides',
    component: Placeholder,
    meta: {
      title: 'nav.slides',
      crumb: 'App & PWA · Slides',
      section: 'storefront',
      icon: 'image',
      sprint: 3,
    },
  },
  {
    path: '/storefront/banners',
    name: 'banners',
    component: Placeholder,
    meta: {
      title: 'nav.banners',
      crumb: 'App & PWA · Banners',
      section: 'storefront',
      icon: 'flag',
      badge: '28',
      sprint: 3,
    },
  },
  {
    path: '/storefront/ads',
    name: 'ads',
    component: Placeholder,
    meta: {
      title: 'nav.ads',
      crumb: 'App & PWA · Ads',
      section: 'storefront',
      icon: 'megaphone',
      badge: '14',
      sprint: 3,
    },
  },
  // ===== AUDIENCE =====
  {
    path: '/audience/users',
    name: 'users',
    component: Placeholder,
    meta: {
      title: 'nav.users',
      crumb: 'Audience · Users',
      section: 'audience',
      icon: 'users',
      badge: '428K',
      privilege: 'check_users',
      sprint: 2,
    },
  },
  {
    path: '/audience/devices',
    name: 'devices',
    component: () => import('@/features/subscriber/pages/DeviceListPage.vue'),
    meta: {
      title: 'nav.devices',
      crumb: 'Audience · Devices',
      section: 'audience',
      icon: 'smartphone',
      badge: '612K',
      privilege: 'devices',
    },
  },
  {
    path: '/audience/stbs',
    name: 'stbs',
    component: Placeholder,
    meta: {
      title: 'nav.stbs',
      crumb: 'Audience · STBs',
      section: 'audience',
      icon: 'tv-2',
      badge: '12K',
      privilege: 'check_users',
      sprint: 2,
    },
  },
  {
    path: '/audience/feedback',
    name: 'feedback',
    component: () => import('@/features/support/pages/FeedbackListPage.vue'),
    meta: {
      title: 'nav.feedback',
      crumb: 'Audience · Feedback',
      section: 'audience',
      icon: 'message-square',
      badge: '148',
      privilege: 'feedbacks',
    },
  },
  // ===== SUPPORT =====
  {
    path: '/support/cases',
    name: 'cases',
    component: Placeholder,
    meta: {
      title: 'nav.cases',
      crumb: 'Support · Cases',
      section: 'support',
      icon: 'life-buoy',
      badge: '184',
      sprint: 3,
    },
  },
  {
    path: '/support/cases/:id',
    name: 'case-detail',
    component: Placeholder,
    meta: {
      title: 'nav.case-detail',
      crumb: 'Support · Case detail',
      section: 'support',
      icon: 'life-buoy',
      sprint: 3,
    },
  },
  {
    path: '/support/refunds',
    name: 'refunds',
    component: Placeholder,
    meta: {
      title: 'nav.refunds',
      crumb: 'Support · Refunds',
      section: 'support',
      icon: 'undo-2',
      badge: '28',
      sprint: 3,
    },
  },
  {
    path: '/support/operator-portal',
    name: 'operator-portal',
    component: Placeholder,
    meta: {
      title: 'nav.operator-portal',
      crumb: 'Support · Telco portals',
      section: 'support',
      icon: 'phone',
      sprint: 3,
    },
  },
  // ===== REVENUE =====
  {
    path: '/revenue/subscriptions',
    name: 'subs',
    component: Placeholder,
    meta: {
      title: 'nav.subs',
      crumb: 'Revenue · Subscriptions',
      section: 'revenue',
      icon: 'credit-card',
      privilege: 'check_users',
      sprint: 2,
    },
  },
  // ── Capability X: Subscription Plans ─────────────────────────────────────
  // Backend: GET/POST /cms/plans, PATCH /cms/plans/:id/price
  // Auth:    PRIV_plans (full prefix per Patch 2 spec)
  {
    path: '/revenue/plans',
    name: 'plans',
    component: () => import('@/features/revenue/views/PlansListView.vue'),
    meta: {
      title: 'nav.plans',
      crumb: 'Revenue · Plans',
      section: 'revenue',
      icon: 'layers',
      badge: '6',
      privilege: 'plans',
    },
  },
  {
    path: '/commerce/packages',
    name: 'vod-packages',
    component: () => import('@/commerce/packages/views/PackagesListView.vue'),
    meta: {
      title: 'nav.vodPackages',
      crumb: 'Commerce · VOD packages',
      section: 'content',
      icon: 'box',
      requiredPrivilege: 'vod_packages',
      navOrder: 50,
      sprint: 3,
    },
  },
  {
    path: '/revenue/billing',
    name: 'billing',
    component: Placeholder,
    meta: {
      title: 'nav.billing',
      crumb: 'Revenue · Billing',
      section: 'revenue',
      icon: 'receipt',
      sprint: 3,
    },
  },
  {
    path: '/revenue/payerrors',
    name: 'payerrors',
    component: Placeholder,
    meta: {
      title: 'nav.payerrors',
      crumb: 'Revenue · Payment errors',
      section: 'revenue',
      icon: 'alert-triangle',
      privilege: 'paid_errors',
      sprint: 3,
    },
  },
  {
    path: '/revenue/offers',
    name: 'offers',
    component: () => import('@/features/marketing/pages/OfferListPage.vue'),
    meta: {
      title: 'nav.offers',
      crumb: 'Revenue · Offers',
      section: 'revenue',
      icon: 'gift',
      privilege: 'offers',
    },
  },
  {
    path: '/revenue/loyalty',
    name: 'loyalty',
    component: Placeholder,
    meta: {
      title: 'nav.loyalty',
      crumb: 'Revenue · Loyalty',
      section: 'revenue',
      icon: 'star',
      sprint: 3,
    },
  },
  {
    path: '/revenue/financials',
    name: 'financials',
    component: Placeholder,
    meta: {
      title: 'nav.financials',
      crumb: 'Revenue · Financials',
      section: 'revenue',
      icon: 'trending-up',
      sprint: 3,
    },
  },
  {
    path: '/revenue/campaigns',
    name: 'campaigns',
    component: () => import('@/features/marketing/pages/CampaignListPage.vue'),
    meta: {
      title: 'nav.campaigns',
      crumb: 'Revenue · Campaigns · CVM',
      section: 'revenue',
      icon: 'target',
      badge: '12',
      privilege: 'campaigns',
    },
  },
  // ── Day 3: Analytics Dashboard ──────────────────────────────────
  // Backend: GET /cms/analytics/dashboard (live in production)
  // Auth:    analytics privilege (bare name, no PRIV_ prefix)
  {
    path: '/analytics',
    name: 'analytics',
    component: () => import('@/features/analytics/views/AnalyticsDashboardPage.vue'),
    meta: {
      title: 'nav.analytics',
      crumb: 'Revenue · Analytics',
      section: 'revenue',
      icon: 'chart-bar',
      privilege: 'analytics',
    },
  },
  // ===== PARTNERS (Capability X) =====
  // Backend: GET/POST /cms/partners, GET /cms/partners/:id
  // Auth:    PRIV_partners (full prefix per Patch 2 spec)
  {
    path: '/video/transcoding',
    name: 'transcoding-dashboard',
    component: () => import('@/features/video/views/TranscodingDashboardView.vue'),
    meta: {
      title: 'nav.transcoding-dashboard',
      crumb: 'Content · Transcoding Dashboard',
      section: 'content',
      icon: 'video',
      layout: 'shell',
      privilege: 'videos',
      hideFromNav: true,
    },
  },
  {
    path: '/video/upload',
    name: 'video-upload',
    component: () => import('@/features/video/views/VideoUploadCenterView.vue'),
    meta: {
      title: 'nav.video-upload',
      crumb: 'Content · Upload Video',
      section: 'content',
      icon: 'upload',
      layout: 'shell',
      privilege: 'videos',
      hideFromNav: true,
    },
  },
  {
    path: '/video/:id',
    name: 'video-detail',
    component: () => import('@/features/video/views/VideoDetailView.vue'),
    meta: {
      title: 'nav.video-detail',
      crumb: 'Content · Video Detail',
      section: 'content',
      icon: 'video',
      layout: 'shell',
      privilege: 'videos',
      hideFromNav: true,
    },
  },
  {
    path: '/partners',
    name: 'partners',
    component: () => import('@/features/revenue/views/PartnerListView.vue'),
    meta: {
      title: 'nav.partners',
      crumb: 'Partners · Directory',
      section: 'partners',
      icon: 'briefcase',
      privilege: 'partners',
    },
  },
  {
    path: '/partners/statements/:id',
    name: 'statement-detail',
    component: () => import('@/features/revenue/views/StatementDetailView.vue'),
    meta: {
      title: 'nav.statement-detail',
      crumb: 'Partners · Statement detail',
      section: 'partners',
      icon: 'file-text',
      privilege: 'partners',
      hideFromNav: true,
    },
  },
  {
    path: '/partners/:id',
    name: 'partner-detail',
    component: () => import('@/features/revenue/views/PartnerDetailView.vue'),
    meta: {
      title: 'nav.partner-detail',
      crumb: 'Partners · Partner detail',
      section: 'partners',
      icon: 'briefcase',
      privilege: 'partners',
      hideFromNav: true,
    },
  },
  // ===== MESSAGING =====
  {
    path: '/messaging/notifications',
    name: 'notifications',
    component: Placeholder,
    meta: {
      title: 'nav.notifications',
      crumb: 'Messaging · Notifications',
      section: 'messaging',
      icon: 'bell',
      sprint: 3,
    },
  },
  // ===== PLATFORM =====
  {
    path: '/platform/reports',
    name: 'reports',
    component: Placeholder,
    meta: {
      title: 'nav.reports',
      crumb: 'Platform · Reports',
      section: 'platform',
      icon: 'bar-chart',
      sprint: 3,
    },
  },
  {
    path: '/platform/settings',
    name: 'settings',
    component: Placeholder,
    meta: {
      title: 'nav.settings',
      crumb: 'Platform · Settings',
      section: 'platform',
      icon: 'settings',
      sprint: 3,
    },
  },
  // ===== ACCOUNT (current user) =====
  {
    path: '/settings/account',
    name: 'account',
    component: AccountView,
    meta: {
      title: 'nav.account',
      crumb: 'Settings · Your account',
      section: 'platform',
      icon: 'user',
      sprint: 2,
    },
  },
  {
    path: '/platform/users',
    name: 'cms-users',
    component: UsersListView,
    meta: {
      title: 'nav.cmsUsers',
      crumb: 'Platform · CMS users',
      section: 'platform',
      icon: 'shield',
      privilege: 'users',
      sprint: 2,
    },
  },
]
const routes: RouteRecordRaw[] = [
  // Auth surface — outside the shell layout.
  {
    path: '/login',
    name: 'login',
    component: Login,
    meta: {
      title: 'auth.login.title',
      crumb: '',
      section: 'overview',
      icon: '',
      public: true,
      layout: 'auth',
    },
  },
  // Root redirect.
  {
    path: '/',
    redirect: { name: 'dashboard' },
  },
  // All application screens, wrapped in the AppShell layout.
  // The shell wrapper itself is added in App.vue via <RouterView>
  // — every screen route here adopts the default `shell` layout
  // unless overridden.
  //
  // Cast through `as RouteRecordRaw` because TypeScript can't
  // narrow `r.meta` (typed as RouteMeta | undefined on the loose
  // RouteRecordRaw) to satisfy the strictened module-augmented
  // RouteMeta — but every entry in SCREENS does in fact populate
  // the required fields, so the cast is sound.
  ...SCREENS.map(
    (r): RouteRecordRaw => ({
      ...r,
      meta: { ...(r.meta ?? {}), layout: 'shell' as const } as RouteRecordRaw['meta'],
    }),
  ),
  // 404 — keep inside the shell for nav consistency.
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: Placeholder,
    meta: {
      title: 'error.notFound',
      crumb: 'Not found',
      section: 'overview',
      icon: 'help-circle',
      layout: 'shell',
    },
  },
]
const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    return savedPosition ?? { top: 0 }
  },
})
router.beforeEach(authGuard)
router.beforeEach(privilegeGuard)
// Capability E — telemetry: track page.view on every completed navigation
export default router
